// /app — gated app surface. Mounts the AppShell after verifying the
// session cookie is valid via /me. Unauthed users bounce to /sign-in
// with a `from` query param so they return to where they tried to go.

import { createFileRoute, redirect } from "@tanstack/react-router";
import { AppShell } from "@/components/AppShell";
import { api, ApiError } from "@/lib/api";
import type { MeResponse } from "@/lib/auth";

export const Route = createFileRoute("/app")({
  // Server-side auth gate via TanStack Router's `beforeLoad`. If /me
  // returns 401, redirect to /sign-in with `?from=<intended path>`.
  beforeLoad: async ({ context, location }) => {
    try {
      // Use the QueryClient context to cache /me across nested routes.
      const me = await context.queryClient.ensureQueryData({
        queryKey: ["me"],
        queryFn: () => api<MeResponse>("/auth/me"),
        staleTime: 60_000,
      });
      // v0.3.15 — new Google users reach /app without accepting the terms
      // or entering a company. Force the signup-completion step first.
      if (me?.needs_onboarding) {
        throw redirect({ to: "/onboarding" });
      }
    } catch (err) {
      // Let router redirects (the onboarding bounce) propagate unchanged.
      if (err && typeof err === "object" && "isRedirect" in err) {
        throw err;
      }
      if (err instanceof ApiError && err.status === 401) {
        throw redirect({
          to: "/sign-in",
          search: { from: location.pathname },
        });
      }
      throw err;
    }
  },
  component: AppShell,
});
